/**
 * DealerCockpit — Jun-2026 (User-Wunsch).
 *
 * Anpassbares Händler-Cockpit mit getrennten Layouts PRO ARBEITSBEREICH
 * (Verkauf · Ankauf · Werkstatt & Allgemein):
 *   • Widgets ein-/ausblenden
 *   • Reihenfolge per Drag & Drop ändern
 *   • je Bereich passende Zusatz-Widgets
 *   • Anzeige-Dichte: Komfort (volle Breite) oder Kompakt (2 Spalten, große Screens)
 *   • Layout + Dichte werden pro Händler gespeichert (dealer-profile)
 *
 * Bewusst ohne externe DnD-Bibliothek (native HTML5 Drag & Drop).
 */
import { useEffect, useMemo, useState } from "react";
import {
    Settings2, GripVertical, Eye, EyeOff, Check, RotateCcw, Loader2,
    LayoutGrid, Rows3, Tag, ShoppingCart, Wrench, RefreshCw,
    Plus, Gavel, Sparkles, Truck, CalendarDays, ClipboardCheck, ScanLine, Boxes,
    FileText, Receipt, Pin, PinOff, Download, Keyboard, Camera, Zap, Calculator,
    ChevronsUpDown, ChevronsDownUp, Cog, Users, Plug, BadgeEuro, ShieldCheck, Smartphone, Gift, Mail, CalendarRange, Send,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import useDealerQuickCounts from "../../lib/useDealerQuickCounts";

import TransactionCodeCard from "./TransactionCodeCard";
import ReferralPerformanceMiniWidget from "./ReferralPerformanceMiniWidget";
import AutoCounterStatsCard from "./AutoCounterStatsCard";
import GuestLeadsCard from "./GuestLeadsCard";
import OrgCockpit from "./OrgCockpit";
import DeliveryForecastCard from "./DeliveryForecastCard";
import AiActivityFeedCard from "./AiActivityFeedCard";
import SalesProfitabilityCard from "./SalesProfitabilityCard";
import TeamWorkloadCard from "./TeamWorkloadCard";
import LeadReactionCard from "./LeadReactionCard";
import TestRideRequestsCard from "./TestRideRequestsCard";
import TestDriveHistoryCard from "./TestDriveHistoryCard";
import OpenTransportsCard from "./OpenTransportsCard";
import SalesCockpitCard from "./SalesCockpitCard";
import DealerStats from "../DealerStats";
import WorkshopBookings from "../WorkshopBookings";
import MissedCallsList from "../MissedCallsList";
import InventoryMarginCard from "./InventoryMarginCard";
import InvoicesOverviewCard from "./InvoicesOverviewCard";
import { MyListings, MyOffers, MyTradeIns, DealerBuyRequests } from "./DashboardTabs";
import { MyDrafts, MyFavorites, MySavedSearches } from "./DashboardSimpleTabs";
import ReservationsList from "../ReservationsList";
import DealerReservationsList from "../DealerReservationsList";
import CollapsibleWidget from "./CollapsibleWidget";
import { setAllCollapsed } from "../../lib/cockpitCollapse";
import TicketsSection from "../tickets/TicketsSection";
import DataQualityCard from "./DataQualityCard";
import { useGearsVisible } from "../../lib/gearVisibility";

// Arbeitsbereiche (Umschalter oben im Cockpit).
const ALL_AREAS = [
    { id: "verkauf", label: "Verkauf", icon: Tag },
    { id: "ankauf", label: "Ankauf", icon: ShoppingCart },
    { id: "werkstatt", label: "Werkstatt", icon: Wrench },
    { id: "disposition", label: "Disposition", icon: Boxes },
    { id: "rechnungen", label: "Buchhaltung", icon: Calculator },
    { id: "system", label: "System", icon: Cog },
];

const WIDGET_LABELS = {
    my_listings: "Meine Inserate",
    drafts: "Entwürfe",
    offers: "Preisvorschläge",
    reservations: "Meine Reservierungen (als Käufer)",
    seller_reservations: "Eingehende Reservierungen",
    favorites: "Favoriten",
    saved_searches: "Gespeicherte Suchen",
    transaction_code: "Transaktions-Code",
    referral_performance: "Referral-Performance",
    guest_leads: "Gast-Leads",
    auto_counter: "Auto-Counter-Wirkung",
    ai_activity: "KI-Aktivitäts-Feed",
    statistics: "Statistik & Performance",
    inventory_margin: "Fahrzeuge im Bestand (Margen)",
    testrides: "Probefahrt-Anfragen",
    open_transports: "Offene Transporte",
    sales_cockpit: "Verkaufs-Cockpit — laufende Verkäufe",
    workshop_today: "Werkstatt-Aufträge",
    workshop_stats: "Werkstatt-Statistik",
    missed_calls: "Verpasste Anrufe",
    trade_ins: "Inzahlungnahmen / Tausch",
    buy_requests: "Ankäufe (B2C / Privat)",
    invoices_overview: "Rechnungs-Übersicht",
    finance_stats: "Rechnungs-Statistik (€Punkte & Rechnungen)",
    team_workload: "Mitarbeiter-Auslastung",
    lead_reaction: "Lead-Reaktionszeit (Woche)",
    data_quality: "Datenqualität — offene Pflegeaufgaben",
};

// Verfügbare Widgets je Bereich (= Default-Reihenfolge).
// Jun-2026 (User-Wunsch) — domänenspezifische Aufteilung:
//   • „Statistik & Performance" (Inserate-KPIs) NUR in Verkauf
//   • „KI-Aktivitäts-Feed" NUR in Ankauf
//   • Werkstatt-Statistik in Werkstatt
//   • „€Punkte-Saldo" + „Meine Rechnungen" (finance_stats) NUR in Rechnungen
const AREA_WIDGETS = {
    verkauf: ["sales_cockpit", "my_listings", "drafts", "offers", "seller_reservations", "transaction_code", "testrides", "guest_leads", "auto_counter", "referral_performance", "team_workload", "lead_reaction", "inventory_margin", "statistics", "ai_activity"],
    ankauf: ["buy_requests", "trade_ins", "reservations", "favorites", "saved_searches", "ai_activity"],
    werkstatt: ["workshop_today", "workshop_stats", "missed_calls", "transaction_code", "ai_activity"],
    disposition: ["sales_cockpit", "delivery_forecast", "data_quality", "open_transports", "inventory_margin"],
    rechnungen: ["invoices_overview", "finance_stats"],
    system: [],
};

// Jun-2026 (User-Wunsch 2b) — Widgets, die bereits einen EIGENEN einklappbaren
// Header besitzen (oder mehrere einklappbare Teil-Blöcke). Diese werden NICHT
// zusätzlich vom Cockpit umhüllt (sonst doppelter Header). Alle übrigen Widgets
// kapselt das Cockpit in einen einklappbaren Rahmen (Standard: eingeklappt).
const SELF_COLLAPSING = new Set([
    "statistics", "finance_stats", "workshop_stats",
    "inventory_margin", "open_transports", "sales_cockpit",
    "buy_requests", "trade_ins",
    "favorites", "saved_searches",
    "ai_activity",
    "delivery_forecast", "data_quality",
]);

// Schmale Widgets (im Kompakt-Modus 1 Spalte); alle anderen spannen 2 Spalten.
const NARROW = new Set(["transaction_code", "referral_performance", "guest_leads", "auto_counter", "missed_calls"]);
// Jul-2026 (Bugfix) — im Kompakt-Modus stehen Widgets 2-spaltig nebeneinander.
// Nur wenige, echte „breite" Widgets (große Tabellen) spannen weiterhin 2 Spalten.
const WIDE = new Set(["my_listings", "drafts", "team_workload", "statistics", "buy_requests", "trade_ins"]);

// Jul-2026 (User-Wunsch) — dezenter Hinweis neben den Unterbereichen von
// Verkauf & Ankauf: wo dieselben Informationen unter „Aufgaben & Hinweise"
// zu finden sind.
const AH_HINTS = {
    verkauf: {
        my_listings: "Was zu tun ist",
        drafts: "Was zu tun ist",
        offers: "Was zu tun ist",
        seller_reservations: "Meine Verkäufe",
        testrides: "Meine Leads",
        guest_leads: "Meine Leads",
        team_workload: "Mitarbeiter-Auslastung",
        statistics: "Auswertungen",
    },
    ankauf: {
        buy_requests: "Meine Aufgaben",
        trade_ins: "Meine Aufgaben",
    },
};

// Jun-2026 (User-Wunsch) — Zuordnung Dashboard-Tab → Cockpit-Arbeitsbereich,
// für die automatische Synchronisation (Auto-Wechsel).
const TAB_TO_AREA = {
    listings: "verkauf", "inventory-margin": "verkauf", drafts: "verkauf",
    offers: "verkauf", reservations: "ankauf", "seller-reservations": "verkauf",
    "test-rides": "verkauf",
    "buy-requests": "ankauf", tradeins: "ankauf", favorites: "ankauf", "saved-searches": "ankauf",
    inbox: "werkstatt", messages: "werkstatt", "missed-calls": "werkstatt",
    "open-transports": "disposition",
};

// Jun-2026 (User-Wunsch) — Schnellaktionen je Arbeitsbereich: macht das Cockpit
// vom Info-Überblick zum Aktions-Startpunkt für den jeweiligen Arbeitsschritt.
// Konsolidierung: die früheren 6 globalen Schnellzugriff-Kacheln (obere Leiste)
// sind hier in die passenden Bereiche integriert (Rechnungen/Direktverkauf →
// Verkauf · Werkstatt-Aufträge/Barcode → Werkstatt · Disposition/Inventur →
// Disposition) inkl. derselben Live-Badges.
const AREA_ACTIONS = {
    verkauf: [
        // Jun-2026 (User-Wunsch): „Neues Inserat" aus dem Cockpit entfernt.
        // Jul-2026 (User-Wunsch): integriertes E-Mail-Center.
        { label: "E-Mail-Center", icon: Mail, to: "/dealer/email" },
    ],
    ankauf: [
        { label: "Preisermittlung (DAT/Schwacke)", icon: Sparkles, to: "/dealer/valuation", primary: true },
    ],
    werkstatt: [
        { label: "Neuer Auftrag", icon: Plus, to: "/dealer/workshop-orders?action=new", primary: true },
        { label: "Schnell-Erfassung", icon: Zap, to: "/dealer/workshop-orders?action=quick-capture" },
        { label: "Scan-Erfassung", icon: Camera, to: "/dealer/workshop-orders?action=zb1-scan" },
        { label: "Lieferschein scannen (KI)", icon: ScanLine, to: "/dealer/wms/scan-delivery" },
        { label: "Direktverkauf", icon: Receipt, to: "/dealer/invoices?action=direct-sale" },
        { label: "Werkstatt-Aufträge", icon: Wrench, to: "/dealer/workshop-orders?status=open", countKey: "workshop_open", countSuffix: "offen" },
        { label: "Werkstatt-Kalender", icon: CalendarDays, to: "/dealer/workshop-calendar" },
    ],
    disposition: [
        { label: "Zur Disposition", icon: Truck, to: "/dealer/disposition", primary: true, countKey: "dispo_todos", countSuffix: "zu erledigen" },
        { label: "Versand-Cockpit", icon: Send, to: "/dealer/versand" },
        { label: "Fahrzeuginventur", icon: ClipboardCheck, to: "/dealer/fahrzeuginventur?filter=uncounted", countKey: "inventory_uncounted", countSuffix: "offen" },
        { label: "Handy-Inventur (QR)", icon: Smartphone, to: "/dealer/fahrzeuginventur?qr=1" },
        { label: "Sammel-Scan Papiere", icon: ScanLine, to: "/dealer/disposition?action=papers-batch-scan" },
    ],
    rechnungen: [
        { label: "Rechnungen öffnen", icon: FileText, to: "/dealer/invoices", primary: true, countKey: "invoices_overdue", countSuffix: "überfällig" },
        { label: "Direktverkauf", icon: Receipt, to: "/dealer/invoices?action=direct-sale" },
        { label: "DATEV-Export", icon: Download, to: "/dealer/invoices?action=datev" },
    ],
    system: [
        { label: "Einstellungen", icon: Settings2, to: "/dashboard?tab=settings", primary: true },
        { label: "Boni-Katalog / Hersteller-Aktionen", icon: Gift, to: "/dashboard?tab=settings#settings-boni-katalog" },
        { label: "Mitarbeiter", icon: Users, to: "/dealer/employees" },
        { label: "Vertretungsplan", icon: CalendarRange, to: "/dealer/vertretungsplan" },
        { label: "Schnittstellen / Export", icon: Plug, to: "/dashboard?tab=settings#settings-market" },
        { label: "Abo / Tarif", icon: BadgeEuro, to: "/dashboard?tab=settings#settings-finanzen" },
        { label: "Kosten & Nutzung", icon: Receipt, to: "/dealer/kosten" },
        { label: "E-Mail-Center", icon: Mail, to: "/dealer/email" },
        { label: "Lohnabrechnung", icon: BadgeEuro, to: "/dealer/lohn" },
        { label: "Sicherheit / 2FA", icon: ShieldCheck, to: "/dashboard?tab=settings#settings-2fa" },
        { label: "DATEV-Stammdaten", icon: Calculator, to: "/dashboard?tab=settings#settings-finanzen" },
    ],
};

function mergeArea(saved, areaId) {
    const avail = AREA_WIDGETS[areaId];
    const availSet = new Set(avail);
    const out = [];
    const seen = new Set();
    for (const e of Array.isArray(saved) ? saved : []) {
        if (e && availSet.has(e.key) && !seen.has(e.key)) {
            out.push({ key: e.key, visible: e.visible !== false });
            seen.add(e.key);
        }
    }
    for (const k of avail) if (!seen.has(k)) out.push({ key: k, visible: true });
    return out;
}

function initLayouts(saved) {
    const obj = saved && !Array.isArray(saved) && typeof saved === "object" ? saved : {};
    const res = {};
    for (const a of ALL_AREAS) res[a.id] = mergeArea(obj[a.id], a.id);
    return res;
}

// Jun-2026 (User-Wunsch) — Status-Kacheln des System-Bereichs: Tarif, 2FA,
// Mitarbeiterzahl und DATEV-Verknüpfung auf einen Blick (grün = ok, amber = Handlungsbedarf).
function SystemStatusTiles() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let active = true;
        api.get("/dealer/system-status")
            .then((r) => { if (active) { setData(r.data); setLoading(false); } })
            .catch(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, []);

    const open = (to) => window.open(to, "_blank", "noopener,noreferrer");

    if (loading) {
        return <div className="flex items-center gap-2 text-sm text-gray-400" data-testid="system-status-loading"><Loader2 className="w-4 h-4 animate-spin" /> Status wird geladen…</div>;
    }
    if (!data) {
        return <p className="text-sm text-gray-400" data-testid="system-status-error">Status konnte nicht geladen werden.</p>;
    }

    const tariffOk = Boolean(data.tariff_name) || data.subscription_status === "active";
    const tiles = [
        { key: "tariff", icon: BadgeEuro, label: "Tarif", value: data.tariff_name || (data.subscription_status === "active" ? "Aktiv" : "Kein Tarif"), state: tariffOk ? "ok" : "warn", to: "/dashboard?tab=settings#settings-finanzen" },
        { key: "2fa", icon: ShieldCheck, label: "Zwei-Faktor (2FA)", value: data.two_factor_enabled ? "Aktiv" : "Aus", state: data.two_factor_enabled ? "ok" : "warn", to: "/dashboard?tab=settings#settings-2fa" },
        { key: "employees", icon: Users, label: "Mitarbeiter", value: `${data.employee_count}`, state: "neutral", to: "/dealer/employees" },
        { key: "datev", icon: Calculator, label: "DATEV-Stammdaten", value: data.datev_linked ? "Verknüpft" : "Nicht verknüpft", state: data.datev_linked ? "ok" : "warn", to: "/dashboard?tab=settings#settings-finanzen" },
    ];
    const styles = {
        ok: "border-green-300 bg-green-50 text-green-800",
        warn: "border-amber-300 bg-amber-50 text-amber-800",
        neutral: "border-gray-200 bg-gray-50 text-gray-700",
    };
    const dot = { ok: "bg-green-500", warn: "bg-amber-500", neutral: "bg-gray-400" };

    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="system-status-tiles">
            {tiles.map((t) => {
                const Icon = t.icon;
                return (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => open(t.to)}
                        className={`text-left rounded-sm border p-3.5 transition-colors hover:shadow-sm ${styles[t.state]}`}
                        data-testid={`system-tile-${t.key}`}
                    >
                        <div className="flex items-center justify-between">
                            <Icon className="w-4 h-4 opacity-70" />
                            <span className={`w-2 h-2 rounded-full ${dot[t.state]}`} />
                        </div>
                        <div className="text-[10px] uppercase tracking-wider font-bold opacity-70 mt-2">{t.label}</div>
                        <div className="font-black text-base mt-0.5" data-testid={`system-tile-${t.key}-value`}>{t.value}</div>
                    </button>
                );
            })}
        </div>
    );
}

export default function DealerCockpit({ user, activeTab, kpis, onOpenGuestInbox, allowedAreas = null }) {    const { refresh } = useAuth();
    const gearsOk = useGearsVisible();

    // Jul-2026 (User-Wunsch) — Mitarbeiter sehen nur die für sie freigeschalteten
    // Cockpit-Bereiche. `allowedAreas` = Liste erlaubter Area-IDs (null = alle).
    const AREAS = allowedAreas ? ALL_AREAS.filter((a) => allowedAreas.includes(a.id)) : ALL_AREAS;
    const quickCounts = useDealerQuickCounts(true);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dragIndex, setDragIndex] = useState(null);
    // Jun-2026 (User-Wunsch) — fester Start-Arbeitsbereich (nur „verkauf"/„ankauf").
    const pinnedStart = ["verkauf", "ankauf"].includes(user?.dashboard_start_area) ? user.dashboard_start_area : null;
    // Jul-2026 (User-Wunsch) — Hauptbereiche standardmäßig ZUGEKLAPPT: die
    // Unterbereiche öffnen sich erst nach Klick auf den Hauptbereich.
    const [area, setArea] = useState(() => pinnedStart || null);
    const [autoSync, setAutoSync] = useState(!pinnedStart);
    const [startArea, setStartArea] = useState(pinnedStart);
    const [savingStart, setSavingStart] = useState(false);
    const [density, setDensity] = useState(user?.dashboard_density === "kompakt" ? "kompakt" : "komfort");
    const [layouts, setLayouts] = useState(() => initLayouts(user?.dashboard_widgets));

    // Auto-Wechsel: folgt der Cockpit-Bereich dem aktiven Dashboard-Tab.
    // React-empfohlenes Muster (State-Anpassung beim Render auf Prop-Änderung)
    // statt useEffect — vermeidet Doppel-Render und Lint-Warnungen.
    const [prevTab, setPrevTab] = useState(activeTab);
    if (activeTab !== prevTab) {
        setPrevTab(activeTab);
        if (autoSync) {
            const mapped = TAB_TO_AREA[activeTab];
            if (mapped && mapped !== area) setArea(mapped);
        }
    }

    // Manueller Bereichswechsel pinnt den Bereich (deaktiviert Auto-Wechsel).
    // Erneuter Klick auf den aktiven Bereich klappt ihn wieder zu.
    const selectArea = (id) => { setArea((cur) => (cur === id ? null : id)); setAutoSync(false); };

    // Jun-2026 (User-Wunsch) — Sprung von den KI-Feed-Kacheln (Ankauf) zum
    // jeweiligen Widget: ggf. Arbeitsbereich wechseln, dann scrollen + kurz
    // hervorheben. Eingeklappte Widgets werden zuvor aufgeklappt.
    const jumpToWidget = (targetArea, testid) => {
        const doScroll = () => {
            const el = document.querySelector(`[data-testid="${testid}"]`);
            if (!el) return;
            const collapsedToggle = el.querySelector('[aria-expanded="false"]');
            if (collapsedToggle) { try { collapsedToggle.click(); } catch { /* ignore */ } }
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            el.classList.add("ring-2", "ring-[#FFCC00]", "ring-offset-2");
            setTimeout(() => el.classList.remove("ring-2", "ring-[#FFCC00]", "ring-offset-2"), 1600);
        };
        if (targetArea && targetArea !== area) {
            selectArea(targetArea);
            setTimeout(doScroll, 300);
        } else {
            doScroll();
        }
    };
    const toggleAuto = () => {
        setAutoSync((on) => {
            const next = !on;
            if (next) { const m = TAB_TO_AREA[activeTab]; if (m) setArea(m); }
            return next;
        });
    };

    const current = layouts[area] || [];

    // Jun-2026 (User-Wunsch) — Tastatur-Shortcuts: „g" dann v/a/w/d/r für den
    // schnellen Bereichswechsel (ignoriert Eingaben in Feldern/Editoren).
    useEffect(() => {
        let gAt = 0;
        const map = { v: "verkauf", a: "ankauf", w: "werkstatt", d: "disposition", r: "rechnungen", s: "system" };
        const onKey = (e) => {
            const tag = (e.target?.tagName || "").toLowerCase();
            if (tag === "input" || tag === "textarea" || tag === "select" || e.target?.isContentEditable) return;
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            const now = Date.now();
            const k = (e.key || "").toLowerCase();
            if (k === "g") { gAt = now; return; }
            if (now - gAt < 1200 && map[k]) {
                e.preventDefault();
                setArea(map[k]);
                setAutoSync(false);
                document.querySelector('[data-testid="dealer-cockpit"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                gAt = 0;
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    // Jun-2026 (User-Wunsch) — Live-Zahlen für die Schnellaktionen.
    const liveCounts = {
        listings_drafts: kpis?.listings?.drafts,
        workshop_open: kpis?.workshop?.open_today,
        dispo_todos: kpis?.dispo?.todos,
        inventory_uncounted: quickCounts?.inventory_uncounted,
        invoices_overdue: kpis?.invoices?.overdue,
        reservations_active: kpis?.reservations?.active,
        buy_requests_unanswered: kpis?.buy_requests?.unanswered,
    };

    // Glanceable Bereichs-Badges am Umschalter: zeigen auf einen Blick, welcher
    // Arbeitsbereich heute Aufmerksamkeit braucht (ersetzen die immer sichtbaren
    // Zähler der früheren oberen Schnellzugriff-Leiste).
    const areaBadge = (id) => {
        if (id === "verkauf") return liveCounts.reservations_active || 0;
        if (id === "ankauf") return liveCounts.buy_requests_unanswered || 0;
        if (id === "rechnungen") return liveCounts.invoices_overdue || 0;
        if (id === "werkstatt") return liveCounts.workshop_open || 0;
        if (id === "disposition") return (liveCounts.dispo_todos || 0) + (liveCounts.inventory_uncounted || 0);
        return 0;
    };

    const renderWidget = (key) => {        switch (key) {
            case "my_listings": return <MyListings userId={user.id} onGotoDisposition={() => jumpToWidget("disposition", "cockpit-area-disposition")} />;
            case "drafts": return <MyDrafts userId={user.id} />;
            case "offers": return <MyOffers currentUser={user} />;
            case "reservations": return <ReservationsList />;
            case "seller_reservations": return <DealerReservationsList />;
            case "favorites": return <MyFavorites />;
            case "saved_searches": return <MySavedSearches />;
            case "transaction_code": return <TransactionCodeCard user={user} />;
            case "referral_performance": return <ReferralPerformanceMiniWidget user={user} />;
            case "guest_leads": return <GuestLeadsCard user={user} onOpenGuestInbox={onOpenGuestInbox} />;
            case "auto_counter": return <AutoCounterStatsCard user={user} />;
            case "testrides": return <><TestRideRequestsCard /><TestDriveHistoryCard /></>;
            case "open_transports": return <OpenTransportsCard />;
            case "sales_cockpit": return <SalesCockpitCard />;
            case "delivery_forecast":
                return <div data-testid="cockpit-widget-delivery_forecast"><DeliveryForecastCard /></div>;
            case "data_quality":
                return <div data-testid="cockpit-widget-data_quality"><DataQualityCard /></div>;
            case "workshop_today": return <WorkshopBookings />;
            case "missed_calls": return <MissedCallsList />;
            case "trade_ins": return <MyTradeIns currentUser={user} />;
            case "buy_requests": return <DealerBuyRequests currentUser={user} />;
            case "inventory_margin":
                return <div data-testid="cockpit-widget-inventory_margin"><InventoryMarginCard /></div>;
            case "team_workload":
                return <div data-testid="cockpit-widget-team_workload"><TeamWorkloadCard /></div>;
            case "lead_reaction":
                return <div data-testid="cockpit-widget-lead_reaction"><LeadReactionCard /></div>;
            case "invoices_overview":
                return <InvoicesOverviewCard kpis={kpis} />;
            case "ai_activity":
                return <AiActivityFeedCard area={area} onJump={jumpToWidget} />;
            case "workshop_stats":
                return <div data-testid="cockpit-widget-workshop_stats"><DealerStats sections={["workshop"]} /></div>;
            case "finance_stats":
                return <div data-testid="cockpit-widget-finance_stats"><DealerStats sections={["finance"]} /></div>;
            case "statistics":
                return (
                    <div id="dealer-stats-section" className="scroll-mt-24 space-y-4" data-testid="cockpit-widget-statistics">
                        <CollapsibleWidget collapseKey="stat_profitability" title="Verkaufs-Rentabilität" defaultCollapsed testid="cockpit-collapse-stat_profitability">
                            <SalesProfitabilityCard />
                        </CollapsibleWidget>
                        <DealerStats sections={["sales"]} />
                    </div>
                );
            default: return null;
        }
    };

    const visibleKeys = useMemo(() => current.filter((w) => w.visible).map((w) => w.key), [current]);

    const updateArea = (fn) => setLayouts((prev) => ({ ...prev, [area]: fn(prev[area] || []) }));
    const toggleVisible = (key) => updateArea((list) => list.map((w) => (w.key === key ? { ...w, visible: !w.visible } : w)));

    const onDrop = (targetIdx) => {
        if (dragIndex !== null && dragIndex !== targetIdx) {
            updateArea((list) => {
                const next = [...list];
                const [moved] = next.splice(dragIndex, 1);
                next.splice(targetIdx, 0, moved);
                return next;
            });
        }
        setDragIndex(null);
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.patch("/users/me/dealer-profile", { dashboard_widgets: layouts, dashboard_density: density });
            await refresh?.();
            toast.success("Cockpit-Layout gespeichert");
            setEditing(false);
        } catch {
            toast.error("Speichern fehlgeschlagen");
        } finally {
            setSaving(false);
        }
    };

    const resetArea = () => updateArea(() => mergeArea(null, area));

    // Jun-2026 (User-Wunsch) — „Als Startansicht merken" (nur Verkauf/Ankauf).
    const saveStartArea = async (val) => {
        setSavingStart(true);
        try {
            await api.patch("/users/me/dealer-profile", { dashboard_start_area: val });
            await refresh?.();
            setStartArea(val);
            if (val) { setArea(val); setAutoSync(false); }
            toast.success(val ? `„${AREAS.find((a) => a.id === val)?.label}" als Startansicht gemerkt` : "Startansicht zurückgesetzt");
        } catch {
            toast.error("Speichern fehlgeschlagen");
        } finally {
            setSavingStart(false);
        }
    };

    return (
        <div className="mt-12 pt-8 border-t border-gray-200" data-testid="dealer-cockpit">
            {/* Mehr-Standort Phase 3 — Org-Cockpit (nur Hauptsitz, lesend aggregiert) */}
            <OrgCockpit user={user} />
            {/* „Vorlauf" ist ins Cockpit → Disposition gewandert (Jul-2026, User-Wunsch). */}
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="font-display font-black text-2xl flex items-center gap-2">
                    <Settings2 className="w-6 h-6 text-[#FFCC00]" /> Mein Cockpit
                </h2>
                <div className="flex items-center gap-2">
                    {/* Dichte-Umschalter (wirkt auf großen Screens) */}
                    <div className="hidden lg:inline-flex rounded-sm border border-gray-300 overflow-hidden" data-testid="cockpit-density-toggle">
                        <button
                            type="button"
                            onClick={() => setDensity("komfort")}
                            className={`inline-flex items-center gap-1.5 h-9 px-3 text-xs font-bold uppercase tracking-wider transition-colors ${density === "komfort" ? "bg-[#111827] text-[#FFCC00]" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                            data-testid="cockpit-density-komfort"
                            title="Volle Breite"
                        >
                            <Rows3 className="w-4 h-4" /> Komfort
                        </button>
                        <button
                            type="button"
                            onClick={() => setDensity("kompakt")}
                            className={`inline-flex items-center gap-1.5 h-9 px-3 text-xs font-bold uppercase tracking-wider transition-colors ${density === "kompakt" ? "bg-[#111827] text-[#FFCC00]" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                            data-testid="cockpit-density-kompakt"
                            title="2 Spalten"
                        >
                            <LayoutGrid className="w-4 h-4" /> Kompakt
                        </button>
                    </div>
                    {!editing ? (
                        gearsOk && <button
                            type="button"
                            onClick={() => setEditing(true)}
                            className="inline-flex items-center gap-1.5 h-9 px-3 bg-white border border-gray-300 hover:bg-gray-50 text-[#111827] font-bold text-xs uppercase tracking-wider rounded-sm transition-colors"
                            data-testid="cockpit-customize-btn"
                        >
                            <Settings2 className="w-4 h-4" /> Dashboard anpassen
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={resetArea}
                                className="inline-flex items-center gap-1.5 h-9 px-3 bg-white border border-gray-300 hover:bg-gray-50 text-gray-600 font-bold text-xs uppercase tracking-wider rounded-sm"
                                data-testid="cockpit-reset-btn"
                            >
                                <RotateCcw className="w-4 h-4" /> Bereich zurücksetzen
                            </button>
                            <button
                                type="button"
                                onClick={save}
                                disabled={saving}
                                className="inline-flex items-center gap-1.5 h-9 px-3 bg-[#111827] hover:bg-black text-[#FFCC00] font-bold text-xs uppercase tracking-wider rounded-sm disabled:opacity-50"
                                data-testid="cockpit-save-btn"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Fertig
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Arbeitsbereich-Umschalter + Auto-Wechsel */}
            <div className="flex items-center gap-1.5 mb-6 flex-wrap" data-testid="cockpit-area-switch">
                <button
                    type="button"
                    onClick={toggleAuto}
                    className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-sm text-xs font-bold uppercase tracking-wider border transition-colors ${autoSync ? "bg-[#111827] text-[#FFCC00] border-[#111827]" : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"}`}
                    title={autoSync ? "Cockpit folgt automatisch dem aktiven Tab — zum Anpinnen Bereich wählen" : "Auto-Wechsel aktivieren (Cockpit folgt dem Tab)"}
                    data-testid="cockpit-auto-toggle"
                >
                    <RefreshCw className={`w-4 h-4 ${autoSync ? "animate-none" : ""}`} /> Auto
                </button>
                <span className="w-px h-5 bg-gray-200 mx-0.5" aria-hidden="true" />
                {AREAS.map((a) => {
                    const Icon = a.icon;
                    const ab = areaBadge(a.id);
                    return (
                        <button
                            key={a.id}
                            type="button"
                            onClick={() => selectArea(a.id)}
                            className={`relative inline-flex items-center gap-1.5 h-9 px-3 rounded-sm text-xs font-bold uppercase tracking-wider border transition-colors ${area === a.id ? "bg-[#FFCC00] text-[#111827] border-[#FFCC00]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
                            data-testid={`cockpit-area-${a.id}`}
                        >
                            <Icon className="w-4 h-4" /> {a.label}
                            {ab > 0 && (
                                <span
                                    className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-mono-num leading-none normal-case tracking-normal"
                                    data-testid={`cockpit-area-badge-${a.id}`}
                                >
                                    {ab > 99 ? "99+" : ab}
                                </span>
                            )}
                        </button>
                    );
                })}
                {/* Jun-2026 (User-Wunsch) — Startansicht merken: nur in Verkauf & Ankauf. */}
                {(area === "verkauf" || area === "ankauf") && (
                    <>
                        <span className="w-px h-5 bg-gray-200 mx-0.5" aria-hidden="true" />
                        <button
                            type="button"
                            onClick={() => saveStartArea(startArea === area ? null : area)}
                            disabled={savingStart}
                            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-sm text-xs font-bold uppercase tracking-wider border transition-colors disabled:opacity-50 ${startArea === area ? "bg-[#FFF7D6] text-[#111827] border-[#FFCC00]" : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"}`}
                            title={startArea === area ? "Diesen Bereich nicht mehr automatisch zuerst öffnen" : "Diesen Bereich beim Öffnen des Dashboards automatisch zuerst anzeigen"}
                            data-testid="cockpit-startarea-btn"
                        >
                            {savingStart ? <Loader2 className="w-4 h-4 animate-spin" /> : (startArea === area ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />)}
                            {startArea === area ? "Startansicht ✓" : "Als Startansicht"}
                        </button>
                    </>
                )}
                <span
                    className="ml-auto text-[10px] text-gray-600 hidden xl:inline-flex items-center gap-1 self-center"
                    title="Tastatur-Shortcut: g, dann v / a / w / d / r für schnellen Bereichswechsel"
                    data-testid="cockpit-shortcuts-hint"
                >
                    <Keyboard className="w-3.5 h-3.5" /> g + v / a / w / d / r / s
                </span>
            </div>

            {/* Schnellaktionen des aktiven Bereichs */}
            {area && (
            <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="cockpit-actions">
                {(AREA_ACTIONS[area] || []).map((act, idx) => {
                    const Icon = act.icon;
                    const cnt = act.countKey ? liveCounts[act.countKey] : null;
                    return (
                        <button
                            key={act.label}
                            type="button"
                            onClick={() => window.open(act.to, "_blank", "noopener,noreferrer")}
                            className={`inline-flex items-center gap-1.5 h-10 px-3.5 rounded-sm text-xs font-black uppercase tracking-wider shadow-sm transition-colors ${act.primary ? "bg-[#111827] hover:bg-black text-[#FFCC00]" : "bg-white border border-gray-300 hover:bg-gray-50 text-[#111827]"}`}
                            data-testid={`cockpit-action-${area}-${idx}`}
                        >
                            <Icon className="w-4 h-4" /> {act.label}
                            {cnt > 0 && (
                                <span
                                    className={`ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-mono-num normal-case tracking-normal ${act.primary ? "bg-[#FFCC00] text-[#111827]" : "bg-[#111827] text-[#FFCC00]"}`}
                                    data-testid={`cockpit-action-count-${area}-${idx}`}
                                >
                                    {cnt} {act.countSuffix}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
            )}

            {/* Anpassen-Modus: sortierbare Liste des aktiven Bereichs */}
            {area && editing && (
                <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 mb-6" data-testid="cockpit-editor">
                    <p className="text-xs text-gray-500 mb-2">
                        Bereich „{AREAS.find((a) => a.id === area)?.label}&ldquo; · Reihenfolge per Ziehen · Augensymbol blendet ein/aus.
                    </p>
                    <ul className="space-y-1.5">
                        {current.map((w, idx) => (
                            <li
                                key={w.key}
                                draggable
                                onDragStart={() => setDragIndex(idx)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => onDrop(idx)}
                                className={`flex items-center gap-2 bg-white border rounded-sm px-2.5 py-2 cursor-move ${dragIndex === idx ? "border-[#FFCC00] ring-1 ring-[#FFCC00]/40" : "border-gray-200"}`}
                                data-testid={`cockpit-editor-item-${w.key}`}
                            >
                                <GripVertical className="w-4 h-4 text-gray-400 shrink-0" />
                                <span className={`flex-1 text-sm font-medium ${w.visible ? "text-[#111827]" : "text-gray-400 line-through"}`}>
                                    {WIDGET_LABELS[w.key] || w.key}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => toggleVisible(w.key)}
                                    className="shrink-0 text-gray-500 hover:text-[#111827]"
                                    title={w.visible ? "Ausblenden" : "Einblenden"}
                                    data-testid={`cockpit-toggle-${w.key}`}
                                >
                                    {w.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Live-Widgets des aktiven Bereichs */}
            {!area ? (
                <p className="text-sm text-gray-400" data-testid="cockpit-collapsed-hint">
                    Bereich oben anklicken (Verkauf, Ankauf, Werkstatt, …), um die Unterbereiche aufzuklappen.
                </p>
            ) : visibleKeys.length === 0 ? (
                area === "system" ? (
                    <SystemStatusTiles />
                ) : (
                    <p className="text-sm text-gray-400" data-testid="cockpit-empty">
                        Keine Widgets in diesem Bereich aktiv — über „Dashboard anpassen&ldquo; wieder einblenden.
                    </p>
                )
            ) : (
                <>
                {!editing && (
                    <div className="flex items-center justify-end gap-2 mb-3" data-testid="cockpit-collapse-controls">
                        <button
                            type="button"
                            onClick={() => setAllCollapsed(false)}
                            className="inline-flex items-center gap-1.5 h-8 px-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-600 font-bold text-[11px] uppercase tracking-wider rounded-sm transition-colors"
                            data-testid="cockpit-expand-all"
                            title="Alle Widgets dieses Bereichs aufklappen"
                        >
                            <ChevronsUpDown className="w-3.5 h-3.5" /> Alle aufklappen
                        </button>
                        <button
                            type="button"
                            onClick={() => setAllCollapsed(true)}
                            className="inline-flex items-center gap-1.5 h-8 px-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-600 font-bold text-[11px] uppercase tracking-wider rounded-sm transition-colors"
                            data-testid="cockpit-collapse-all"
                            title="Alle Widgets dieses Bereichs einklappen"
                        >
                            <ChevronsDownUp className="w-3.5 h-3.5" /> Alle einklappen
                        </button>
                    </div>
                )}
                <div
                    className={density === "kompakt" ? "lg:grid lg:grid-cols-2 lg:gap-4 lg:items-start space-y-4 lg:space-y-0" : "space-y-4"}
                    data-testid="cockpit-widgets"
                >
                    {visibleKeys.map((key) => {
                        const ahHint = (AH_HINTS[area] || {})[key];
                        return (
                        <div key={`${area}-${key}`} className={density === "kompakt" && WIDE.has(key) ? "lg:col-span-2" : ""}>
                            {SELF_COLLAPSING.has(key) ? (
                                <>
                                    {ahHint && (
                                        <div className="flex justify-end mb-0.5">
                                            <span className="text-[10px] text-gray-400" data-testid={`cockpit-ah-hint-${key}`}>
                                                auch in: Aufgaben &amp; Hinweise → {ahHint}
                                            </span>
                                        </div>
                                    )}
                                    {renderWidget(key)}
                                </>
                            ) : (
                                <CollapsibleWidget
                                    collapseKey={`w_${key}`}
                                    title={WIDGET_LABELS[key] || key}
                                    hint={ahHint ? `auch in: Aufgaben & Hinweise → ${ahHint}` : null}
                                    defaultCollapsed
                                    testid={`cockpit-collapse-${key}`}
                                >
                                    {renderWidget(key)}
                                </CollapsibleWidget>
                            )}
                        </div>
                        );
                    })}
                </div>
                </>
            )}

            {/* Support / Tickets — aus den Einstellungen hierher ins Dashboard verschoben (User-Wunsch). */}
            <div className="mt-6" id="dashboard-support" data-testid="dashboard-support">
                <TicketsSection />
            </div>
        </div>
    );
}
